// src/components/events/GuestList.tsx
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

import SparkHeading from '../common/SparkHeading';
import SparkButton  from '../common/SparkButton';
import SparkCard    from '../common/SparkCard';
import SparkFooter  from '../common/SparkFooter';

interface GuestListProps {
  eventId: string;
  recipients: Array<{ name: string; email?: string; phone?: string }>;
  onSendComplete: () => void;
  onSkip: () => void;
  onBack: () => void;
  // Add these new props
  invitationLink?: string;
  onCopyLink?: () => void;
}

const GuestList: React.FC<GuestListProps> = ({
  eventId,
  recipients,
  onSendComplete,
  onSkip,
  onBack,
  // Add the new props here
  invitationLink,
  onCopyLink,
}) => {
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /* ---------------- send ---------------------------------------- */
  const handleSend = async () => {
    if (!recipients.length) {
      setError('No recipients to send invitations to.');
      return;
    }

    if (recipients.length > 200) {
      setError('Each event can have up to 200 guests. Please remove some before sending.');
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      await httpsCallable(functions, 'sendInvitations')({ eventId, recipients });
      setSuccess('Invitations sent successfully!');
      setTimeout(onSendComplete, 1500);
    } catch (e: any) {
      setError(e.message || 'Failed to send invitations. Please try again.');
    } finally {
      setSending(false);
    }
  };

  /* ---------------- render -------------------------------------- */
  return (
    <>
      {/*   BLOCK 1 : heading / alerts   */}
      <div style={{ width: '100%', marginBottom: '2rem' }}>
        <SparkHeading level="h3">
          Guest List ({recipients.length})
        </SparkHeading>

        {error && (
          <div className="spark-message spark-message--error" style={{ marginTop: '1rem' }}>
            {error}
          </div>
        )}
        {success && (
          <div className="spark-message spark-message--success" style={{ marginTop: '1rem' }}>
            {success}
          </div>
        )}
      </div>

      {/*   BLOCK 2 : list card   */}
      <SparkCard
        variant="solid"
        style={{ width: '100%', marginBottom: '2.5rem' }}   // <- extra gap
      >
        {recipients.length ? (
          <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0 }}>
            {recipients.map((r, i) => (
              <li
                key={i}
                style={{
                  padding: '.5rem 0',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#000',
                  borderBottom: i < recipients.length - 1 ? '1px solid #e5e7eb' : 'none',
                }}
              >
                {r.name}{' '}
                <span style={{ fontWeight: 400 }}>
                  — {r.email || r.phone || 'No contact info'}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ textAlign: 'center', fontWeight: 500, color: '#000' }}>
            No guests added yet.
          </p>
        )}
      </SparkCard>

      {/*   BLOCK 3 : primary action   */}
      <SparkButton
        onClick={handleSend}
        disabled={sending || !recipients.length}
        fullWidth
        variant="violet"
        style={{ marginBottom: '1rem' }}  // <- reduced from 2.5rem to 1rem
      >
        {sending ? 'SENDING…' : `SEND TO ALL GUESTS (${recipients.length})`}
      </SparkButton>

      {/*   BLOCK 3.5 : copy link button (NEW)   */}
      {invitationLink && onCopyLink && (
        <SparkButton
          onClick={onCopyLink}
          fullWidth
          variant="secondary"
          style={{ marginBottom: '2.5rem' }}  // <- maintains spacing before footer
        >
          COPY LINK & SHARE
        </SparkButton>
      )}

      {/*   BLOCK 4 : nav footer   */}
      <SparkFooter style={{ marginTop: '0' /* gap now handled above */ }}>
        <SparkButton variant="secondary" onClick={onBack}>
          BACK
        </SparkButton>
        <SparkButton onClick={onSkip}>SKIP</SparkButton>
      </SparkFooter>
    </>
  );
};

export default GuestList;